import React from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import type {
  ExtractedContractDetails,
  ExpiryStatus,
  NewRepositoryContract,
  ContractCategory,
  SignatureStatus
} from '../../../services/api/contractService';
import { CONTRACT_CATEGORIES } from '../../../utils/contractCategories';
import { SignatureNotice } from './signature';

export interface RepositoryContractFormValues {
  supplierId: string;
  groupId: string;
  contractTitle: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  category: string;
  versionLabel: string;
  notes: string;
  /** Not edited in the form: what the AI read, saved with the document. */
  signatureStatus: SignatureStatus | '';
  signatureNotes: string;
}

export const EMPTY_FORM: RepositoryContractFormValues = {
  supplierId: '',
  groupId: '',
  contractTitle: '',
  contractNumber: '',
  startDate: '',
  endDate: '',
  category: '',
  versionLabel: '',
  notes: '',
  signatureStatus: '',
  signatureNotes: ''
};

/** The request body for saving these form values as a new repository contract. */
export const toNewRepositoryContract = (values: RepositoryContractFormValues): NewRepositoryContract => ({
  supplierId: values.supplierId,
  groupId: values.groupId || undefined,
  contractTitle: values.contractTitle.trim(),
  contractNumber: values.contractNumber.trim() || undefined,
  startDate: values.startDate,
  endDate: values.endDate,
  category: (values.category || undefined) as ContractCategory | undefined,
  versionLabel: values.versionLabel.trim() || undefined,
  notes: values.notes.trim() || undefined,
  signatureStatus: values.signatureStatus || undefined,
  signatureNotes: values.signatureNotes || undefined
});

type FieldErrors = Partial<Record<keyof RepositoryContractFormValues, string>>;

export const validateForm = (values: RepositoryContractFormValues): FieldErrors => {
  const errors: FieldErrors = {};
  if (!values.supplierId) errors.supplierId = 'Pick the supplier';
  if (!values.contractTitle.trim()) errors.contractTitle = 'Enter the contract name';
  if (!values.startDate) errors.startDate = 'Enter the start date';
  if (!values.endDate) errors.endDate = 'Enter the end date';
  if (values.startDate && values.endDate && values.endDate < values.startDate) {
    errors.endDate = 'The end date is before the start date';
  }
  return errors;
};

// Mirrors the server's rule (backend/src/utils/contractExpiry.ts) for a preview only;
// the repository always shows the server's answer.
const EXPIRING_SOON_MONTHS = 6;
const previewStatus = (endDate: string): ExpiryStatus | null => {
  if (!endDate) return null;
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const end = new Date(`${endDate}T00:00:00Z`);
  if (isNaN(end.getTime())) return null;
  if (end < today) return 'expired';
  const cutoff = new Date(today);
  cutoff.setUTCMonth(cutoff.getUTCMonth() + EXPIRING_SOON_MONTHS);
  return end <= cutoff ? 'expiring_soon' : 'active';
};

const STATUS_PREVIEW: Record<ExpiryStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'text-emerald-700 dark:text-emerald-300' },
  expiring_soon: { label: 'Expiring soon', className: 'text-amber-700 dark:text-amber-300' },
  expired: { label: 'Expired', className: 'text-rose-700 dark:text-rose-300' }
};

const baseInput =
  'w-full px-3 py-2 text-sm bg-white dark:bg-dark-bg-secondary border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors';

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  /** The AI wasn't confident about this value. */
  uncertain?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: (className: string) => React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, required, uncertain, error, hint, children }) => {
  const border = error
    ? 'border-rose-400 dark:border-rose-500'
    : uncertain
      ? 'border-amber-400 dark:border-amber-500 bg-amber-50/50 dark:bg-amber-900/10'
      : 'border-gray-300 dark:border-dark-border';
  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
        {required && <span className="text-rose-500" aria-hidden>*</span>}
        {uncertain && !error && (
          <span className="inline-flex items-center gap-1 text-xs font-normal text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3" aria-hidden />
            Check this
          </span>
        )}
      </label>
      {children(`${baseInput} ${border}`)}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-rose-600 dark:text-rose-400">{error}</p>
      ) : (
        hint && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>
      )}
    </div>
  );
};

interface RepositoryContractFormProps {
  values: RepositoryContractFormValues;
  onChange: (values: RepositoryContractFormValues) => void;
  errors: FieldErrors;
  suppliers: { id: string | number; name: string }[];
  groups: { id: string; name: string }[];
  /** What the AI found, if the document was read. */
  extracted: ExtractedContractDetails | null;
  /** Whether the AI's supplier name matched an existing supplier. */
  supplierMatched: boolean;
  /** Prefix for element ids, so the form never collides with other controls on the page. */
  idPrefix?: string;
}

export const RepositoryContractForm: React.FC<RepositoryContractFormProps> = ({
  values,
  onChange,
  errors,
  suppliers,
  groups,
  extracted,
  supplierMatched,
  idPrefix = 'repo-form'
}) => {
  const set = (field: keyof RepositoryContractFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onChange({ ...values, [field]: e.target.value });
  const uncertain = (field: ExtractedContractDetails['uncertainFields'][number]) =>
    Boolean(extracted?.uncertainFields.includes(field));
  const status = previewStatus(values.endDate);
  const fid = (field: string) => `${idPrefix}-${field}`;
  const describedBy = (field: keyof RepositoryContractFormValues) => (errors[field] ? `${fid(field)}-error` : undefined);

  return (
    <div className="space-y-4">
      <SignatureNotice extracted={extracted} />
      {extracted && (
        <div className="flex gap-3 p-3 text-sm rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-200 border border-primary-100 dark:border-primary-900/40">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="space-y-1">
            <p>Filled in from the document. Check each field before saving{extracted.uncertainFields.length > 0 && ', especially the ones marked'}.</p>
            {extracted.notes && <p className="text-primary-800/80 dark:text-primary-200/80">{extracted.notes}</p>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          id={fid('supplierId')}
          label="Supplier"
          required
          uncertain={uncertain('supplierName') || Boolean(extracted?.supplierName && !supplierMatched)}
          error={errors.supplierId}
          hint={
            extracted?.supplierName && !supplierMatched ? (
              <>The contract names <strong>{extracted.supplierName}</strong>, which doesn't match a supplier. Pick one, or add it under Master Data first.</>
            ) : undefined
          }
        >
          {(cls) => (
            <select id={fid('supplierId')} value={values.supplierId} onChange={set('supplierId')} className={cls} aria-invalid={Boolean(errors.supplierId)} aria-describedby={describedBy('supplierId')}>
              <option value="">Select a supplier</option>
              {suppliers.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
          )}
        </Field>

        <Field
          id={fid('groupId')}
          label="Group"
          uncertain={uncertain('groupName')}
          hint={extracted?.groupName && !values.groupId ? <>The contract mentions <strong>{extracted.groupName}</strong>.</> : undefined}
        >
          {(cls) => (
            <select id={fid('groupId')} value={values.groupId} onChange={set('groupId')} className={cls}>
              <option value="">No group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          )}
        </Field>

        <Field id={fid('contractTitle')} label="Contract name" required uncertain={uncertain('contractTitle')} error={errors.contractTitle}>
          {(cls) => (
            <input id={fid('contractTitle')} type="text" value={values.contractTitle} onChange={set('contractTitle')} maxLength={200} className={cls} aria-invalid={Boolean(errors.contractTitle)} aria-describedby={describedBy('contractTitle')} />
          )}
        </Field>

        <Field id={fid('contractNumber')} label="Contract number" uncertain={uncertain('contractNumber')}>
          {(cls) => (
            <input id={fid('contractNumber')} type="text" value={values.contractNumber} onChange={set('contractNumber')} maxLength={100} className={cls} />
          )}
        </Field>

        <Field id={fid('startDate')} label="Start date" required uncertain={uncertain('startDate')} error={errors.startDate}>
          {(cls) => (
            <input id={fid('startDate')} type="date" value={values.startDate} onChange={set('startDate')} className={cls} aria-invalid={Boolean(errors.startDate)} aria-describedby={describedBy('startDate')} />
          )}
        </Field>

        <Field
          id={fid('endDate')}
          label="End date"
          required
          uncertain={uncertain('endDate')}
          error={errors.endDate}
          hint={status && <>Will show as <span className={`font-medium ${STATUS_PREVIEW[status].className}`}>{STATUS_PREVIEW[status].label}</span></>}
        >
          {(cls) => (
            <input id={fid('endDate')} type="date" value={values.endDate} min={values.startDate || undefined} onChange={set('endDate')} className={cls} aria-invalid={Boolean(errors.endDate)} aria-describedby={describedBy('endDate')} />
          )}
        </Field>

        <Field id={fid('category')} label="Category" uncertain={uncertain('category')}>
          {(cls) => (
            <select id={fid('category')} value={values.category} onChange={set('category')} className={cls}>
              <option value="">Not set</option>
              {CONTRACT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          )}
        </Field>

        <Field id={fid('versionLabel')} label="Document label">
          {(cls) => (
            <input id={fid('versionLabel')} type="text" value={values.versionLabel} onChange={set('versionLabel')} placeholder="e.g. Executed copy" maxLength={100} className={cls} />
          )}
        </Field>
      </div>

      <Field id={fid('notes')} label="Notes">
        {(cls) => (
          <textarea id={fid('notes')} value={values.notes} onChange={set('notes')} rows={2} maxLength={1000} className={cls} />
        )}
      </Field>
    </div>
  );
};

export default RepositoryContractForm;
